/**
 * Create a customs form doc definition for use with pdfmake.
 * @param {Object} – Form data structure
 */
export const createDocDefinition = (data) => {
  return {
    content: [
      {
        text: getDocCustomsTitle(
          data.prefersCN23,
          data.parsedTotalValue,
          data.usesNationalCarrier
        ),
        style: "subheader",
      },
      "May be opened officially",
      ...(() => {
        if (
          shouldUseFullContent(
            data.prefersCN23,
            data.parsedTotalValue,
            data.usesNationalCarrier
          )
        ) {
          return [
            { text: "FROM", style: "tableTitle" },
            {
              style: "table",
              table: {
                widths: [200, "*"],
                body: [
                  [{ text: "Full name", bold: true }, data.origin.fullName],
                  [
                    { text: "Business name", bold: true },
                    data.origin.businessName,
                  ],
                  [
                    { text: "Phone number", bold: true },
                    data.origin.phoneNumber,
                  ],
                  [
                    { text: "Address line 1", bold: true },
                    data.origin.addressLineFirst,
                  ],
                  [
                    { text: "Address line 2", bold: true },
                    data.origin.addressLineSecond,
                  ],
                  [{ text: "Postal code", bold: true }, data.origin.postalCode],
                  [{ text: "City", bold: true }, data.origin.city],
                  [{ text: "Country", bold: true }, data.origin.country],
                  [{ text: "EORI number", bold: true }, data.eori || "–"],
                  [
                    { text: data.eoriUK ? "EORI UK Number" : null, bold: true },
                    data.eoriUK,
                  ],
                ],
              },
            },
            { text: "TO", style: "tableTitle" },
            {
              style: "table",
              table: {
                widths: [200, "*"],
                body: [
                  [
                    { text: "Full name", bold: true },
                    data.destination.fullName,
                  ],
                  [
                    { text: "Business name", bold: true },
                    data.destination.businessName,
                  ],
                  [
                    { text: "Phone number", bold: true },
                    data.destination.phoneNumber,
                  ],
                  [
                    { text: "Address line 1", bold: true },
                    data.destination.addressLineFirst,
                  ],
                  [
                    { text: "Address line 2", bold: true },
                    data.destination.addressLineSecond,
                  ],
                  [
                    { text: "Postal code", bold: true },
                    data.destination.postalCode,
                  ],
                  [{ text: "City", bold: true }, data.destination.city],
                  [{ text: "Country", bold: true }, data.destination.country],
                ],
              },
            },
          ];
        } else {
          return [];
        }
      })(),
      { text: "CONTENT", style: "tableTitle" },
      {
        style: "table",
        table: {
          widths: [100, "*", "*", "*", "*", "*"],
          body: [
            [
              { text: "Detailed description", bold: true },
              { text: "Quantity", bold: true },
              { text: "Net Weight (KG)", bold: true },
              { text: "Value (€)", bold: true },
              { text: "HS Code", bold: true },
              { text: "Origin", bold: true },
            ],
            ...data.items.map((item) => [
              item.description,
              item.quantity,
              item.weight,
              item.value * item.quantity,
              item.hsCode || "–",
              item.originCountry,
            ]),
            [
              "Total",
              "",
              { text: data.totalWeight, bold: true },
              { text: data.totalValue, bold: true },
              "",
              "",
            ],
          ],
        },
      },
      {
        style: "table",
        table: {
          widths: ["*", "*"],
          body: [
            [
              { text: "Category of item", bold: true },
              data.categoryOfItem ? data.categoryOfItem.join(", ") : "–",
            ],
            [
              { text: "Office of origin", bold: true },
              data.officeOfOrigin || "–",
            ],
            [{ text: "Incoterms", bold: true }, data.incoterms || "–"],
            [{ text: "Invoice number", bold: true }, data.invoiceNumber || "–"],
          ],
        },
      },
      ...(() => {
        if (data.comments) {
          return [
            {
              style: "table",
              table: {
                widths: ["*", "*"],
                body: [[{ text: "Comments", bold: true }, data.comments]],
              },
            },
          ];
        } else {
          return [];
        }
      })(),
      {
        style: "table",
        table: {
          widths: ["*", "*"],
          body: [
            [
              {
                text: "I certify that the particulars given in this customs declaration are correct and that this item does not contain any dangerous article or articles prohibited by legislation or by postal or customs regulations.",
                colSpan: 2,
              },
            ],
          ],
        },
      },
      {
        text: `Date of signature: ${data.date}`,
        fontSize: 10,
      },
      {
        image: data.signatureData,
        fit: [120, 120],
      },
    ],
    styles: {
      header: {
        fontSize: 16,
        bold: true,
        margin: [0, 0, 0, 10],
      },
      subheader: {
        fontSize: 12,
        bold: true,
        margin: [0, 10, 0, 10],
      },
      tableTitle: {
        fontSize: 11,
        bold: true,
        margin: [0, 15, 0, 0],
      },
      table: {
        fontSize: 9,
        margin: [0, 10, 0, 5],
      },
      tableHeader: {
        bold: true,
        fontSize: 11,
        color: "black",
      },
    },
    info: {
      title: "CN22/CN23/Commercial invoice Customs Declaration",
      author: "Generated on Sendcloud.com",
      subject: "Customs declaration",
      keywords: "CN22, CN23, Commercial invoice, Customs declaration",
    },
    permissions: {
      printing: "highResolution", //'lowResolution'
      modifying: true,
      copying: true,
      annotating: true,
      fillingForms: true,
      contentAccessibility: true,
      documentAssembly: true,
    },
  };
};

/**
 * Create a customs form document title
 * @param {Boolean} prefersCN23
 * @param {Number} totalValue
 * @param {String} usesNationalCarrier
 */
export const getDocCustomsTitle = (
  prefersCN23,
  totalValue,
  usesNationalCarrier
) => {
  if (usesNationalCarrier === "Yes") {
    return totalValue <= 425 && !prefersCN23
      ? "Customs Declaration – CN22"
      : "Customs Declaration – CN23";
  } else {
    return "Customs Declaration – Commercial Invoice";
  }
};

/**
 * Determines whether to render CN22 or CN23 full content
 * @param {Boolean} prefersCN23
 * @param {Number} totalValue
 * @param {String} usesNationalCarrier
 */
export const shouldUseFullContent = (
  prefersCN23,
  totalValue,
  usesNationalCarrier
) => {
  if (usesNationalCarrier === "Yes") {
    return totalValue >= 425 || prefersCN23;
  } else {
    return false;
  }
};
